import React from 'react';
import {
  Button,
  Flex,
  Text,
  useColorMode,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  Icon,
  Grid,
  Input,
  FormControl,
} from '@chakra-ui/react';
import { CaretDown, PlusCircle, XCircle } from '@phosphor-icons/react';
import { Formik } from 'formik';

interface FilterTextInputPillProps {
  clear: () => void;
  setValue: (value: string) => void;
  initialValue: string;
  label: string;
  placeholder?: string;
  isDisabled?: boolean;
}

export const FilterTextInputPill: React.FC<FilterTextInputPillProps> = ({
  isDisabled,
  clear,
  initialValue,
  label,
  placeholder = 'Eingabe...',
  setValue,
}) => {
  const { colorMode } = useColorMode();

  const getSelectedText = () => {
    if (!initialValue || initialValue.trim() === '') return false;
    // Truncate if too long
    if (initialValue.length > 20) {
      return initialValue.substring(0, 20) + '...';
    }
    return initialValue;
  };

  const selectedText = getSelectedText();

  return (
    <Popover placement="bottom-start">
      {/* @ts-ignore */}
      <PopoverTrigger>
        <Flex
          border={selectedText ? '1px solid' : '1px dashed'}
          borderColor="gray.300"
          color={colorMode === 'light' ? 'gray.500' : 'gray.400'}
          alignItems="center"
          gap="1"
          rounded="full"
          fontSize="xs"
          fontWeight="semibold"
          px="1.5"
          py="0.5"
          cursor="pointer"
          transition="all"
          pointerEvents={isDisabled ? 'none' : 'all'}
          opacity={isDisabled ? '0.2' : '1'}
          _hover={{
            bg: colorMode === 'light' ? 'gray.100' : 'gray.700',
            color: colorMode === 'light' ? 'gray.600' : 'gray.200',
          }}
        >
          {selectedText ? (
            <Icon
              as={XCircle}
              h="3"
              w="3"
              onClick={(event) => {
                event.stopPropagation();
                clear();
              }}
            />
          ) : (
            <Icon
              as={PlusCircle}
              h="3"
              w="3"
              _hover={{
                color: 'gray.900',
              }}
            />
          )}
          <Text as="span">{label}</Text>
          {selectedText ? (
            <>
              <Text as="span" fontSize="10px">{` | `}</Text>
              <Text as="span" color="blue.500">
                {selectedText}
              </Text>
              <Icon
                as={CaretDown}
                h="4"
                w="4"
                _hover={{
                  color: 'gray.900',
                }}
              />
            </>
          ) : (
            ''
          )}
        </Flex>
      </PopoverTrigger>

      <PopoverContent className="bg-white dark:bg-gray-900 shadow-2xl border border-gray-200 dark:border-gray-800 rounded p-3" w="300px">
        <>
          <PopoverArrow />
          <Formik
            initialValues={{
              value: initialValue || '',
            }}
            enableReinitialize
            onSubmit={(values) => {
              setValue(values.value);
            }}
          >
            {({ values, setFieldValue, handleSubmit }) => {
              return (
                <Grid p="2" gap="3">
                  <FormControl>
                    <Input
                      size="sm"
                      placeholder={placeholder}
                      value={values.value}
                      onChange={(e) => setFieldValue('value', e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          handleSubmit();
                        }
                      }}
                    />
                  </FormControl>

                  <Button colorScheme="teal" size="sm" fontWeight="medium" w="full" onClick={() => handleSubmit()}>
                    Speichern
                  </Button>
                </Grid>
              );
            }}
          </Formik>
        </>
      </PopoverContent>
    </Popover>
  );
};
